import { Capacitor } from '@capacitor/core';

/**
 * Meta Pixel + Conversions API, for signup conversion tracking.
 *
 * WEB ONLY, deliberately. Meta Pixel is ADVERTISING tracking, which on iOS falls
 * under App Tracking Transparency: shipping it inside the native build would
 * require an ATT prompt and a change to the app's privacy declaration, and
 * submitting with undeclared tracking is a review rejection. Mixpanel already in
 * this app is analytics, which is a different category — this is not the same
 * step. Every entry point below no-ops on native.
 *
 * Both halves fire for one signup, sharing an event_id so Meta DEDUPLICATES them
 * into a single conversion:
 *
 *   Pixel (browser)  carries fbp/fbc, the cookies that actually attribute a
 *                    conversion to an ad click. Blocked by ad blockers and ITP
 *                    for a large share of users.
 *   CAPI (server)    cannot be blocked, so it is the one that reliably counts.
 *                    It has no cookies of its own, which is why the client hands
 *                    it fbp/fbc — server-side alone attributes poorly.
 *
 * Neither runs until VITE_META_PIXEL_ID is set. Absent, this file is inert.
 */

/** From .env at build time. Absent → the whole integration is off. */
const PIXEL_ID = import.meta.env.VITE_META_PIXEL_ID as string | undefined;

declare global {
  interface Window {
    fbq?: any;
    _fbq?: any;
  }
}

let initialised = false;

/** Web, with a Pixel ID configured. Anything else is a no-op. */
function enabled(): boolean {
  return !!PIXEL_ID && !Capacitor.isNativePlatform() && typeof window !== 'undefined';
}

/** Read a cookie the Pixel sets. Absent before the Pixel has run, or if blocked. */
function cookie(name: string): string {
  try {
    const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    return match ? decodeURIComponent(match[2]) : '';
  } catch {
    return '';
  }
}

export const MetaService = {
  /**
   * Load the Pixel. Safe to call more than once; safe to call when disabled.
   * Call at boot, so fbp exists by the time anything is tracked.
   */
  init(): void {
    if (!enabled() || initialised) return;
    try {
      /* eslint-disable */
      // Meta's standard loader, transcribed rather than pasted as an opaque
      // blob: it defines the fbq queue so calls made before the script has
      // downloaded are replayed rather than lost.
      (function (f: any, b: Document, e: string, v: string) {
        if (f.fbq) return;
        const n: any = (f.fbq = function () {
          n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
        });
        if (!f._fbq) f._fbq = n;
        n.push = n;
        n.loaded = true;
        n.version = '2.0';
        n.queue = [];
        const t = b.createElement(e) as HTMLScriptElement;
        t.async = true;
        t.src = v;
        const s = b.getElementsByTagName(e)[0];
        s.parentNode?.insertBefore(t, s);
      })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
      /* eslint-enable */

      window.fbq('init', PIXEL_ID);
      window.fbq('track', 'PageView');
      initialised = true;
      console.log('[MetaService] Pixel initialised');
    } catch (e) {
      console.warn('[MetaService] Pixel init failed:', e);
    }
  },

  /**
   * A completed signup.
   *
   * Fired when onboarding FINISHES rather than when the auth record appears: an
   * abandoned half-signup is not a conversion, and counting it would quietly
   * inflate every cost-per-registration figure the ad account reports.
   *
   * Never throws and never blocks. A tracking call must not be able to fail the
   * thing it is tracking.
   */
  async trackSignup(params: {
    userId: string;
    email?: string | null;
    country?: string | null;
    /** The signer-up's own ageGroup === 'adult'. Anything unknown is a no. */
    isAdult?: boolean;
  }): Promise<void> {
    if (!enabled()) return;

    // Consent before either send.
    //
    // The Pixel matters as much as the relay here: it fires in the WebView, so
    // on iOS it would otherwise track without the ATT answer, and it never
    // reaches the server that enforces the age rule. Both are behind this.
    const { resolveMetaConsent } = await import('./metaConsent');
    const consent = await resolveMetaConsent(params.isAdult === true);
    if (!consent.allowed) {
      console.log(
        `[MetaService] Skipping: platform=${consent.platform} `
        + `adult=${params.isAdult === true} att=${consent.attAuthorized}`,
      );
      return;
    }

    // Shared between the two sends so Meta counts one conversion, not two.
    const eventId = `signup_${params.userId}_${Date.now()}`;

    try {
      window.fbq?.('track', 'CompleteRegistration', {}, { eventID: eventId });
    } catch (e) {
      console.warn('[MetaService] Pixel event failed:', e);
    }

    // The server half. Handed the cookies and the page URL because it has
    // neither: a CAPI event without fbp/fbc is counted but barely attributed.
    try {
      const { getFunctions, httpsCallable } = await import('firebase/functions');
      await httpsCallable(getFunctions(), 'sendMetaConversion')({
        eventName: 'CompleteRegistration',
        eventId,
        eventSourceUrl: window.location.href,
        userId: params.userId,
        email: params.email || '',
        country: params.country || '',
        fbp: cookie('_fbp'),
        fbc: cookie('_fbc'),
        // The server re-checks age itself; these two are the facts only the
        // device holds, and it fails closed on both.
        platform: consent.platform,
        attAuthorized: consent.attAuthorized,
      });
    } catch (e) {
      console.warn('[MetaService] Conversions API relay failed:', e);
    }
  },
};
